"use client";

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import {
  COLOR_MODE_ATTRIBUTE,
  COLOR_MODE_STORAGE_KEY,
  ColorMode,
  parseColorMode,
} from "@/utils/colorMode";

interface ColorModeContextType {
  colorMode: ColorMode;
  isDarkMode: boolean;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextType | undefined>(undefined);

function applyColorMode(mode: ColorMode): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute(COLOR_MODE_ATTRIBUTE, mode);
}

function getInitialColorMode(): ColorMode {
  if (typeof window === "undefined") return "light";
  try {
    return parseColorMode(window.localStorage.getItem(COLOR_MODE_STORAGE_KEY));
  } catch {
    return "light";
  }
}

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [colorMode, setColorModeState] = useState<ColorMode>(getInitialColorMode);
  const pathname = usePathname() || "/";
  // Public survey pages render their own appearance theme and own the
  // <html data-color-mode> attribute while mounted. Skip writing it from
  // here so the survey's theme isn't immediately overridden by the
  // visitor's cached platform preference.
  const isPublicSurveyRoute = pathname.startsWith("/survey/");

  useEffect(() => {
    if (isPublicSurveyRoute) return;
    applyColorMode(colorMode);
    try {
      window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, colorMode);
    } catch {
      // Ignore storage failures and still apply mode in-memory.
    }
  }, [colorMode, isPublicSurveyRoute]);

  const value = useMemo<ColorModeContextType>(
    () => ({
      colorMode,
      isDarkMode: colorMode === "dark",
      setColorMode: setColorModeState,
      toggleColorMode: () =>
        setColorModeState((previous) =>
          previous === "dark" ? "light" : "dark",
        ),
    }),
    [colorMode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = (): ColorModeContextType => {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error("useColorMode must be used within a ColorModeProvider");
  }
  return context;
};
